import { useContext, useEffect, useState, useRef } from 'react';
import axios from "axios";
import { Link, navigate } from 'raviger';
import { Button, Card, CardGroup, Col, Collapse, ListGroup, Modal, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import { DATE_FORMAT } from '../constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDownToSquare } from '@fortawesome/pro-regular-svg-icons';
import {
  faArrowAltCircleLeft,
  faChevronCircleDown,
  faChevronCircleUp,
  faFileCsv,
  faInfoCircle,
  faList,
} from '@fortawesome/free-solid-svg-icons';
import * as Yup from 'yup';
import { Formik } from "formik";
import { useSubmitting } from '../hooks';
import Header from '../components/Header';
import { titleCase } from '../components/Utils';
import { capitalize } from '../utils/formatString';
import { useLocationWithRoutes } from '../hooks';
import { SystemErrorContext } from '../components/SystemError';
import { AuthContext } from "../accounts/AccountsReducer";
import { printAnimalCareSchedule, printAllAnimalCareSchedules, printAnimalSummary } from '../animals/Utils';
import LoadingLink from "../components/LoadingLink";
import { Checkbox, DropDown } from '../components/Form';
import ActionsDropdown from '../components/ActionsDropdown';
import ShelterlyPrintifyButton from '../components/ShelterlyPrintifyButton';
import { faRightLeft } from '@fortawesome/pro-solid-svg-icons';

function ShelterAnimalList({ id, organization, incident }) {

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const [data, setData] = useState({unroomed_animals:[], buildings:[], total_count: 0, isFetching: false});
  const [organizationData, setOrganizationData] = useState({
    name: '',
    short_name: '',
    liability_name: '',
    liability_short_name: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showBuildings, setShowBuildings] = useState({'unroomed':false});
  const [showRooms, setShowRooms] = useState({});
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedAnimals, setSelectedAnimals] = useState([]);
  const { getFullLocationFromPath } = useLocationWithRoutes();
  const {
    handleSubmitting,
    submittingComplete,
  } = useSubmitting();
  const [showTransferAnimalConfirm, setShowTransferAnimalConfirm] = useState(false);
  const handleTransferAnimalClose = () => setShowTransferAnimalConfirm(false);
  const [shelters, setShelters] = useState({options: [], shelters: [], room_options: {}, isFetching: false});

  const roomRef = useRef(null);

  function buildAnimalUrl(animal) {
    return getFullLocationFromPath(`/${organization}/${incident}/animals/${animal.id_for_incident}`)
  }

  const handleDownloadCarePdfClick = ({...animal}) => {
    // const animal = data.animals.find((animal) => animal.id === animalId);
    animal.url = buildAnimalUrl(animal);
    printAnimalCareSchedule(animal);
  }

  const handleDownloadAllCarePdfClick = (animals) => {
    let animal_data = animals.map((animal) => ({
      ...animal,
      url: buildAnimalUrl(animal)
    }))
    printAllAnimalCareSchedules(animal_data)
    .then(submittingComplete)
    .finally(() => setIsLoading(false));
  }

  const handleDownloadSummaryPdfClick = async (animals) => {
    setIsLoading(true);
    // wait for 1 tick so spinner will set before the print button locks up the browser
    await new Promise(resolve => setTimeout(resolve, 0));

    printAnimalSummary({'animals':animals, 'organization':organizationData, 'incident':state.incident.name})
      .finally(() => setIsLoading(false));
  }

  const downloadCsv = (title, animals) => {
    const headers = [
      'ID',
      'Name',
      'Count',
      'Species',
      'Status',
      'Sex',
      'Age',
      'Size',
      'Primary Color',
      'Secondary Color',
      'Intake ID',
      'Microchip',
      'External ID',
      'Fixed',
      'Injured',
      'Confined',
      'Aggressive',
      'Special Handling',
      'Intake Date',
      'Shelter',
      'Building',
      'Room',
    ];

    const rows = animals.map(item => {
      return [
        "A#" + String(item.id_for_incident),
        item.name || "Unknown Name",
        item.animal_count,
        item.species_string,
        item.status,
        item.sex||"N/A",
        item.age||"N/A",
        item.size||"N/A",
        item.pcolor||"N/A",
        item.scolor||"N/A",
        item.intake_id||"N/A",
        item.microchip||"N/A",
        item.external_id||"N/A",
        item.fixed||"N/A",
        item.injured||"N/A",
        item.confined||"N/A",
        item.aggressive||"N/A",
        item.aco_required||"N/A",
        item.intake_date ? moment(item.intake_date).format('MM/DD/YYYY HH:mm') : "N/A",
        data.name||"N/A",
        item.building_name||"N/A",
        item.room_name||"N/A",
      ];
    });

    const csv = [
      headers.join(','),
      ...rows.map(row =>
        row.map(value =>
          `"${String(value ?? '').replace(/"/g, '""')}"`
        ).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = title.replace(/\.csv$/i, '') + `-${moment().format(DATE_FORMAT)}.csv`;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchShelterData = async () => {
      // Fetch Shelter Details data.
      await axios.get('/shelter/api/shelter/' + id + '/?incident=' + incident + '&organization=' + organization + '&room_assign=true', {
          cancelToken: source.token,
      })
      .then(response => {
          if (!unmounted) {
            let count = 0;
            let show_buildings = {'unroomed':true};
            let show_rooms = {};
            response.data.unroomed_animals.forEach(unroomed_animal => {
                count = count + unroomed_animal.animal_count;
            });
            response.data['unroomed_count'] = count;
            response.data.buildings.forEach((building, index) => {
              let building_count = 0;
              building.rooms.forEach(room => {
                building_count = building_count + room.animal_count;
                show_rooms[room.id] = room.animal_count > 0;
              });
              response.data.buildings[index]['animal_count'] = building_count;
              show_buildings[building.id] = building_count > 0;
            });
            setData(response.data);
            setShowBuildings(show_buildings);
            setShowRooms(show_rooms);
          }
      })
      .catch(error => {
          if (!unmounted) {
          setShowSystemError(true);
          }
      })
      .finally(() => setIsLoading(false));
    };
    fetchShelterData();

    const fetchOrganizationData = async () => {
      // Fetch Organization data.
      await axios.get('/incident/api/organization/?slug=' + organization, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          setOrganizationData(response.data[0]);
      }})
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };

    
    fetchOrganizationData();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, []);

  const fetchShelters = () => {
    let unmounted = false;
    let source = axios.CancelToken.source();
    setShelters({options: [], shelters: [], room_options: {}, isFetching: true});
    // Fetch Shelter data.
    axios.get('/shelter/api/shelter/?incident=' + incident + '&organization=' + organization, {
      cancelToken: source.token,
    })
    .then(response => {
      if (!unmounted) {
        let options = [];
        let room_options = {};
        response.data.filter(shelter => Number(shelter.id) !== Number(id)).forEach(shelter => {
          // Build shelter option list.
          options.push({value: shelter.id, label: shelter.name});
          room_options[shelter.id] = [];
          shelter.buildings.forEach(building => {
            building.rooms.forEach(room => {
              // Build room option list identified by shelter ID.
              room_options[shelter.id].push({value: room.id, label: room.building_name + ' - ' + room.name + ' (' + (room.animal_count || 0) + ' animals)'});
            });
          });
        });
        setShelters({options: options, shelters:response.data.filter(shelter => Number(shelter.id) !== Number(id)), room_options:room_options, isFetching:false});
      }
    })
    .catch(error => {
      if (!unmounted) {
        setShelters({options: [], shelters: [], room_options: {}, isFetching: false});
        setShowSystemError(true);
      }
    });
  };

  useEffect(() => {
    if (showTransferAnimalConfirm && shelters.options.length === 0) {
      fetchShelters();
    }
  }, [showTransferAnimalConfirm]);

  return (
    <div className="ms-2 me-2">
    <Header>
      <span id="back" style={{cursor:'pointer'}} onClick={(e) => {setSelectedAnimals([]);setSelectedBuilding(null); selectedBuilding ? console.log() : navigate("/" + organization + "/" + incident + "/shelter/" + id)}} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>
      {data.name} - {selectedBuilding ? "Building" : "Shelter"} List View
    </Header>
    {selectedBuilding ? 
    <Row className="d-flex">
      <Col>
        <Card className="border rounded d-flex" style={{width:"100%", height: "100%"}}>
          <Card.Body>
            <Card.Title>
              <h4>Building Information
                {isLoading ? (
                  <Spinner
                    className="align-self-center me-3 float-end"
                    {...{
                      as: 'span',
                      animation: 'border',
                      size: undefined,
                      role: 'status',
                      'aria-hidden': 'true',
                      variant: 'light',
                      style: {
                        height: '1.5rem',
                        width: '1.5rem',
                        marginBottom: '0.75rem'
                      }
                    }}
                  />
                ) : (
                  <ActionsDropdown variant="secondary" title="Actions" className="float-end" style={{marginTop:"-4px"}}>
                    <LoadingLink
                      onClick={() => {setSelectedAnimals([]);setSelectedBuilding(null)}}
                      className="d-block"
                      isLoading={false}
                      label="Shelter List View"
                      >
                        <FontAwesomeIcon icon={faList} className="fas" inverse />
                    </LoadingLink>
                    <LoadingLink
                      onClick={() => {setShowTransferAnimalConfirm(true);}}
                      className="d-block"
                      isLoading={false}
                      label={"Transfer Animals Between Shelters"}
                      disabled={selectedAnimals.length === 0}
                      disabled_text="Must have animals selected."
                      >
                      <FontAwesomeIcon icon={faRightLeft} className="fas" inverse />
                    </LoadingLink>
                    {/* <ShelterlyPrintifyButton
                      id="animal-details-animal-care-schedule"
                      spinnerSize={2.0}
                      tooltipPlacement='right'
                      label='Download All Animal Care Schedules'
                      printFunc={() => handleDownloadAllCarePdfClick(data.unroomed_animals)}
                      disabled={isLoading}
                      noOverlay={true}
                    /> */}
                    <LoadingLink onClick={() => {downloadCsv(`${data.name}-animal-list.csv`, [...data.unroomed_animals, ...data.buildings.flatMap(building => building.rooms.flatMap(room => room.animals.flatMap(animal => animal)))])}} label="Download Shelter List" disabled={isLoading} isLoading={isLoading} className="d-block">
                      <FontAwesomeIcon icon={faFileCsv} className="fas" style={{cursor:'pointer'}} inverse />
                    </LoadingLink>
                  </ActionsDropdown>
                )}
              </h4>
            </Card.Title>
            <p className="phr" />
                {/* {selectedBuilding ? <h3 className="mt-3" style={{marginBottom:"-10px"}}>{data.buildings.filter(building => building.id === selectedBuilding)[0].name} ({data.buildings.filter(building => building.id === selectedBuilding)[0]?.animal_count} Animal{data.buildings.filter(building => building.id === selectedBuilding)[0]?.animal_count === 1 ? '' : 's'})</h3> : ""} */}
            <ListGroup variant="flush" style={{marginTop:"-13px", marginBottom:"-13px"}}>
              <ListGroup.Item>
                <b>Building: </b> {data.buildings.filter(building => building.id === selectedBuilding)[0].name}
              </ListGroup.Item>
              <ListGroup.Item>
                <b>Currently Sheltering: </b>{data.buildings.filter(building => building.id === selectedBuilding)[0]?.animal_count} Animal{data.buildings.filter(building => building.id === selectedBuilding)[0]?.animal_count === 1 ? '' : 's'}
              </ListGroup.Item>
            </ListGroup>
          </Card.Body>
        </Card>
      </Col>
    </Row>
    :
    <Row className="d-flex">
      <Col>
        <Card className="border rounded d-flex" style={{width:"100%", height: "100%"}}>
          <Card.Body>
            <Card.Title>
              <h4>Shelter Information
                {isLoading ? (
                  <Spinner
                    className="align-self-center me-3 float-end"
                    {...{
                      as: 'span',
                      animation: 'border',
                      size: undefined,
                      role: 'status',
                      'aria-hidden': 'true',
                      variant: 'light',
                      style: {
                        height: '1.5rem',
                        width: '1.5rem',
                        marginBottom: '0.75rem'
                      }
                    }}
                  />
                ) : (
                  <ActionsDropdown variant="secondary" title="Actions" className="float-end" style={{marginTop:"-4px"}}>
                    {selectedBuilding ? <LoadingLink
                      onClick={() => {setSelectedAnimals([]);setSelectedBuilding(null)}}
                      className="d-block"
                      isLoading={false}
                      label="Shelter List View"
                      >
                        <FontAwesomeIcon icon={faList} className="fas" inverse />
                    </LoadingLink> : ""}
                    <LoadingLink
                      onClick={() => {setShowTransferAnimalConfirm(true);}}
                      className="d-block"
                      isLoading={false}
                      label={"Transfer Animals Between Shelters"}
                      disabled={selectedAnimals.length === 0}
                      disabled_text="Must have animals selected."
                      >
                      <FontAwesomeIcon icon={faRightLeft} className="fas" inverse />
                    </LoadingLink>
                    {/* <ShelterlyPrintifyButton
                      id="animal-details-animal-care-schedule"
                      spinnerSize={2.0}
                      tooltipPlacement='right'
                      label='Download All Animal Care Schedules'
                      printFunc={() => handleDownloadAllCarePdfClick(data.unroomed_animals)}
                      disabled={isLoading}
                      noOverlay={true}
                    /> */}
                    <LoadingLink onClick={() => {downloadCsv(`${data.name}-animal-list.csv`, [...data.unroomed_animals, ...data.buildings.flatMap(building => building.rooms.flatMap(room => room.animals.flatMap(animal => animal)))])}} label="Download Shelter List" disabled={isLoading} isLoading={isLoading} className="d-block">
                      <FontAwesomeIcon icon={faFileCsv} className="fas" style={{cursor:'pointer'}} inverse />
                    </LoadingLink>
                  </ActionsDropdown>
                )}
              </h4>
            </Card.Title>
            <p className="phr" />
            <ListGroup variant="flush" style={{marginTop:"-13px", marginBottom:"-13px"}}>
              <ListGroup.Item>
                <b>Shelter: </b> {data.name}
              </ListGroup.Item>
              <ListGroup.Item>
                <b>Currently Sheltering: </b>{data.animal_count || 0} Animal{data.animal_count === 1 ? "" : "s"}
              </ListGroup.Item>
            </ListGroup>
          </Card.Body>
        </Card>
      </Col>
    </Row>}
    {!selectedBuilding ? <div className="row mt-3">
      <div className="col-12 d-flex">
        <Card className="border rounded" style={{width:"100%"}}>
          <Card.Body style={{marginTop:"-10px", marginBottom:"-5px"}}>
            <div className="d-flex justify-content-between" style={{marginBottom:"-3px"}}>
              <h4 className="h4 mb-0 pb-0 pt-2">
                Roomless ({data.unroomed_count} Animal{data.unroomed_count === 1 ? '' : 's'})
                <FontAwesomeIcon icon={faChevronCircleDown} hidden={showBuildings['unroomed']} onClick={() => {setShowBuildings({...showBuildings, ['unroomed']:true})}} className="ms-1" size="sm" style={{cursor:'pointer'}} inverse />
                <FontAwesomeIcon icon={faChevronCircleUp} hidden={!showBuildings['unroomed']} onClick={() => {setShowBuildings({...showBuildings, ['unroomed']:false})}} className="ms-1" size="sm" style={{cursor:'pointer'}} inverse />
              </h4>
              <ActionsDropdown variant="secondary" title="Actions" className="float-end" style={{marginTop:"2px"}}>
                <LoadingLink
                  href={"/" + organization + "/" + incident + "/shelter/" + id + "/assign"}
                  className="d-block"
                  isLoading={false}
                  label="Room Animals"
                  >
                    <FontAwesomeIcon icon={faArrowDownToSquare} className="fas" inverse />
                </LoadingLink>
                {/* <ShelterlyPrintifyButton
                  id="animal-details-animal-care-schedule"
                  spinnerSize={2.0}
                  tooltipPlacement='right'
                  label='Download Roomless Animal Care Schedules'
                  printFunc={() => handleDownloadAllCarePdfClick(data.unroomed_animals)}
                  disabled={isLoading}
                  noOverlay={true}
                /> */}
                {data.unroomed_count > 0 ? <LoadingLink onClick={() => {downloadCsv(`roomless-animal-list.csv`, [...data.unroomed_animals])}} label="Download Roomless List" disabled={isLoading} isLoading={isLoading} className="d-block">
                  <FontAwesomeIcon icon={faFileCsv} className="fas" style={{cursor:'pointer'}} inverse />
                </LoadingLink> : ""}
              </ActionsDropdown>
            </div>
            <Collapse in={showBuildings['unroomed']}>
              <div>
                <p className="phr" />
                <Row className="flex-nowrap" style={{marginTop:"-5px"}}>
                  <Col className="ps-0 pe-0" style={{minWidth:"50px", maxWidth:"50px", marginLeft:"12px"}}>
                    Select
                  </Col>
                  <Col style={{minWidth:"75px", maxWidth:"75px", marginLeft:"-11px"}}>
                    ID
                  </Col>
                  <Col style={{minWidth:"250px", maxWidth:"250px", marginLeft:"1px"}}>
                    Name
                  </Col>
                  <Col style={{minWidth:"150px", maxWidth:"150px", marginLeft:"1px"}}>
                    Species
                  </Col>
                  <Col style={{minWidth:"55px", maxWidth:"55px"}} className="pe-0">
                    Count
                  </Col>
                  <Col style={{minWidth:"50px", maxWidth:"50px"}} className="pe-0">
                    Sex
                  </Col>
                  <Col style={{minWidth:"135px", maxWidth:"135px"}} className="pe-0">
                    Color
                  </Col>
                  <Col style={{minWidth:"245px", maxWidth:"245px"}}>
                    Room
                  </Col>
                </Row>
                {data.unroomed_animals.map(animal => (
                  <Card key={animal.id} className="rounded mb-1" style={{height:"32px", marginLeft:"12px", maxWidth:"741px"}}>
                    <div className="row no-gutters flex-nowrap me-0">
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", borderTopLeftRadius:"0.25rem", borderBottomLeftRadius:"0.25rem", minWidth:"50px", maxWidth:"50px", backgroundColor:"#615e5e"}}>
                        <Checkbox
                          id={'select' + animal.id}
                          name={'select' + animal.id}
                          style={{
                            transform: "scale(.75)",
                            marginRight: "1px",
                            marginTop: "-6px",
                            marginBottom: "-5px",
                            paddingLeft:"13px"
                          }}
                          onChange={() => {
                            if (selectedAnimals.includes(animal.id)) {
                              setSelectedAnimals(selectedAnimals.filter(animal_id => animal_id !== animal.id));
                            }
                            else {
                              setSelectedAnimals(selectedAnimals.concat(animal.id));
                            }
                          }}
                        />
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"75px", maxWidth:"75px", backgroundColor:"#615e5e"}} title={animal.id_for_incident}>
                        <Link href={"/" + organization + "/" + incident + "/animals/" + animal.id_for_incident} className="text-link" style={{textDecoration:"none", color:"white"}}>A#{animal.id_for_incident}</Link>
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"250px", maxWidth:"250px", backgroundColor:"#615e5e"}} title={animal.name || "Unknown Name"}>
                        {animal.name || "Unknown Name"}
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"150px", maxWidth:"150px", backgroundColor:"#615e5e"}} title={capitalize(animal.species_string_singular)}>
                        {capitalize(animal.species_string_singular)}
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"55px", maxWidth:"55px", backgroundColor:"#615e5e"}} title={animal.animal_count}>
                        {animal.animal_count}
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"50px", maxWidth:"50px", backgroundColor:"#615e5e"}} title={animal.sex||"N/A"}>
                        {animal.sex||""}
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"135px", maxWidth:"135px", backgroundColor:"#615e5e", textTransform:"capitalize"}} title={(animal.pcolor + (animal.pcolor && animal.scolor ? " / " : "") + animal.scolor) || "N/A"}>
                        {(animal.pcolor + (animal.pcolor && animal.scolor ? " / " : "") + animal.scolor) || "N/A"}
                      </Col>
                      <Col className="border" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"245px", maxWidth:"245px", borderTopRightRadius:"0.25rem", borderBottomRightRadius:"0.25rem", backgroundColor:"#615e5e"}} title={'N/A'}>
                        {'N/A'}
                      </Col>
                    </div>
                  </Card>
                ))}
              </div>
            </Collapse>
          </Card.Body>
        </Card>
      </div>
    </div> : ""}
    {!selectedBuilding && data.buildings.map(building => (
      <>
      <div className="row mt-3 mb-3">
        <div className="col-12 d-flex">
          <Card className="border rounded" style={{width:"100%"}}>
            <Card.Body style={{marginTop:"-10px", marginBottom:"-5px"}}>
              <div className="d-flex justify-content-between" style={{marginBottom:"-3px"}}>
                <h4 className="h4 mb-0 pb-0 pt-2">
                  <Link href={"/" + organization + "/" + incident + "/shelter/building/" + building.id} className="text-link" style={{textDecoration:"none", color:"white"}}>{building.name}</Link> ({building.animal_count} Animal{building.animal_count === 1 ? '' : 's'})
                  {building.animal_count > 0 ? <span>
                    <FontAwesomeIcon icon={faChevronCircleDown} hidden={showBuildings[building.id]} onClick={() => {setShowBuildings({...showBuildings, [building.id]:true})}} className="ms-1" size="sm" style={{cursor:'pointer'}} inverse />
                    <FontAwesomeIcon icon={faChevronCircleUp} hidden={!showBuildings[building.id]} onClick={() => {setShowBuildings({...showBuildings, [building.id]:false})}} className="ms-1" size="sm" style={{cursor:'pointer'}} inverse />
                  </span> : ""}
                </h4>
                <ActionsDropdown variant="secondary" title="Actions" className="float-end" style={{marginTop:"2px"}}>
                  <LoadingLink
                    onClick={() => {setSelectedAnimals([]);setSelectedBuilding(building.id)}}
                    className="d-block"
                    isLoading={false}
                    label="Building List View"
                    >
                      <FontAwesomeIcon icon={faList} className="fas" inverse />
                  </LoadingLink>
                  <LoadingLink
                    href={"/" + organization + "/" + incident + "/shelter/" + id + "/assign?building_id=" + building.id}
                    className="d-block"
                    isLoading={false}
                    label="Room Animals"
                    >
                      <FontAwesomeIcon icon={faArrowDownToSquare} className="fas" inverse />
                  </LoadingLink>
                  {/* {building.animal_count > 0 ? <ShelterlyPrintifyButton
                    id="animal-details-animal-care-schedule"
                    spinnerSize={2.0}
                    tooltipPlacement='right'
                    label='Download Building Animal Care Schedules'
                    printFunc={() => handleDownloadAllCarePdfClick(data.unroomed_animals)}
                    disabled={isLoading}
                    noOverlay={true}
                  /> : ""} */}
                  {building.animal_count > 0 ? <LoadingLink onClick={() => {downloadCsv(`${building.name}-animal-list.csv`, [...building.rooms.flatMap(room => room.animals.flatMap(animal => animal))])}} label="Download Building List View" disabled={isLoading} isLoading={isLoading} className="d-block">
                    <FontAwesomeIcon icon={faFileCsv} className="fas" style={{cursor:'pointer'}} inverse />
                  </LoadingLink> : ""}
                </ActionsDropdown>
              </div>
              <Collapse in={showBuildings[building.id]}>
                <div>
                  <p className="phr" />
                  <Row className="flex-nowrap" style={{marginTop:"-5px"}}>
                    <Col className="ps-0 pe-0" style={{minWidth:"50px", maxWidth:"50px", marginLeft:"12px"}}>
                      Select
                    </Col>
                    <Col style={{minWidth:"75px", maxWidth:"75px", marginLeft:"-11px"}}>
                      ID
                    </Col>
                    <Col style={{minWidth:"250px", maxWidth:"250px", marginLeft:"1px"}}>
                      Name
                    </Col>
                    <Col style={{minWidth:"150px", maxWidth:"150px", marginLeft:"1px"}}>
                      Species
                    </Col>
                    <Col style={{minWidth:"55px", maxWidth:"55px"}} className="pe-0">
                      Count
                    </Col>
                    <Col style={{minWidth:"50px", maxWidth:"50px"}} className="pe-0">
                      Sex
                    </Col>
                    <Col style={{minWidth:"135px", maxWidth:"135px"}} className="pe-0">
                      Color
                    </Col>
                    <Col style={{minWidth:"245px", maxWidth:"245px"}}>
                      Room
                    </Col>
                  </Row>
                  {building.rooms.map(room => (
                  <>
                  {room.animals.map(animal => (
                  <Card key={animal.id} className="rounded mb-1" style={{height:"32px", marginLeft:"12px", maxWidth:"741px"}}>
                    <div className="row no-gutters flex-nowrap me-0">
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", borderTopLeftRadius:"0.25rem", borderBottomLeftRadius:"0.25rem", minWidth:"50px", maxWidth:"50px", backgroundColor:"#615e5e"}}>
                        <Checkbox
                          id={'select' + animal.id}
                          name={'select' + animal.id}
                          style={{
                            transform: "scale(.75)",
                            marginRight: "1px",
                            marginTop: "-6px",
                            marginBottom: "-5px",
                            paddingLeft:"13px"
                          }}
                          onChange={() => {
                            if (selectedAnimals.includes(animal.id)) {
                              setSelectedAnimals(selectedAnimals.filter(animal_id => animal_id !== animal.id));
                            }
                            else {
                              setSelectedAnimals(selectedAnimals.concat(animal.id));
                            }
                          }}
                        />
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"75px", maxWidth:"75px", backgroundColor:"#615e5e"}} title={animal.id_for_incident}>
                        <Link href={"/" + organization + "/" + incident + "/animals/" + animal.id_for_incident} className="text-link" style={{textDecoration:"none", color:"white"}}>A#{animal.id_for_incident}</Link>
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"250px", maxWidth:"250px", backgroundColor:"#615e5e"}} title={animal.name || "Unknown Name"}>
                        {animal.name || "Unknown Name"}
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"150px", maxWidth:"150px", backgroundColor:"#615e5e"}} title={capitalize(animal.species_string_singular)}>
                        {capitalize(animal.species_string_singular)}
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"55px", maxWidth:"55px", backgroundColor:"#615e5e"}} title={animal.animal_count}>
                        {animal.animal_count}
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"50px", maxWidth:"50px", backgroundColor:"#615e5e"}} title={animal.sex||"N/A"}>
                        {animal.sex||"N/A"}
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"135px", maxWidth:"135px", backgroundColor:"#615e5e", textTransform:"capitalize"}} title={(animal.pcolor + (animal.pcolor && animal.scolor ? " / " : "") + animal.scolor) || "N/A"}>
                        {(animal.pcolor + (animal.pcolor && animal.scolor ? " / " : "") + animal.scolor) || "N/A"}
                      </Col>
                      <Col className="border" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"245px", maxWidth:"245px", borderTopRightRadius:"0.25rem", borderBottomRightRadius:"0.25rem", backgroundColor:"#615e5e"}} title={room.name}>
                        {room.name}
                      </Col>
                    </div>
                  </Card>
                  ))}
                </>
                ))}
                </div>
              </Collapse>
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
    ))}
    {/* {selectedBuilding ? <h3 className="mt-3" style={{marginBottom:"-10px"}}>{data.buildings.filter(building => building.id === selectedBuilding)[0].name} ({data.buildings.filter(building => building.id === selectedBuilding)[0]?.animal_count} Animal{data.buildings.filter(building => building.id === selectedBuilding)[0]?.animal_count === 1 ? '' : 's'})</h3> : ""} */}
    {data.buildings.filter(building => building.id === selectedBuilding)[0]?.rooms.map(room => (
    <>
      <div className="row mt-3 mb-3">
        <div className="col-12 d-flex">
          <Card className="border rounded" style={{width:"100%"}}>
            <Card.Body style={{marginTop:"-10px", marginBottom:"-5px"}}>
              <div className="d-flex justify-content-between" style={{marginBottom:"-3px"}}>
                <h4 className="h4 mb-0 pb-0 pt-2">
                  <Link href={"/" + organization + "/" + incident + "/shelter/room/" + room.id} className="text-link" style={{textDecoration:"none", color:"white"}}>{room.name}</Link> ({room.animal_count||"0"} Animal{room.animal_count === 1 ? '' : 's'})
                  {room.animal_count > 0 ? <span>
                    <FontAwesomeIcon icon={faChevronCircleDown} hidden={showRooms[room.id]} onClick={() => {setShowRooms({...showRooms, [room.id]:true})}} className="ms-1" size="sm" style={{cursor:'pointer'}} inverse />
                    <FontAwesomeIcon icon={faChevronCircleUp} hidden={!showRooms[room.id]} onClick={() => {setShowRooms({...showRooms, [room.id]:false})}} className="ms-1" size="sm" style={{cursor:'pointer'}} inverse />
                  </span> : ""}
                </h4>
                <ActionsDropdown variant="secondary" title="Actions" className="float-end" style={{marginTop:"2px"}}>
                  <LoadingLink
                    href={"/" + organization + "/" + incident + "/shelter/" + id + "/assign?building_id=" + room.building}
                    className="d-block"
                    isLoading={false}
                    label="Room Animals"
                    >
                      <FontAwesomeIcon icon={faArrowDownToSquare} className="fas" inverse />
                  </LoadingLink>
                  {/* {room.animal_count > 0 ? <ShelterlyPrintifyButton
                    id="animal-details-animal-care-schedule"
                    spinnerSize={2.0}
                    tooltipPlacement='right'
                    label='Download Room Animal Care Schedules'
                    printFunc={() => handleDownloadAllCarePdfClick(room.animals)}
                    disabled={isLoading}
                    noOverlay={true}
                  /> : ""} */}
                  {room.animal_count > 0 ? <LoadingLink onClick={() => {downloadCsv(`${room.name}-animal-list.csv`, [...room.animals.flatMap(animal => animal)])}} label="Download Room List" disabled={isLoading} isLoading={isLoading} className="d-block">
                    <FontAwesomeIcon icon={faFileCsv} className="fas" style={{cursor:'pointer'}} inverse />
                  </LoadingLink> : ""}
                </ActionsDropdown>
              </div>
              <Collapse in={showRooms[room.id]}>
                <div>
                  <p className="phr" />
                  <Row className="flex-nowrap" style={{marginTop:"-5px"}}>
                    <Col className="ps-0 pe-0" style={{minWidth:"50px", maxWidth:"50px", marginLeft:"12px"}}>
                      Select
                    </Col>
                    <Col style={{minWidth:"75px", maxWidth:"75px", marginLeft:"-11px"}}>
                      ID
                    </Col>
                    <Col style={{minWidth:"250px", maxWidth:"250px", marginLeft:"1px"}}>
                      Name
                    </Col>
                    <Col style={{minWidth:"150px", maxWidth:"150px", marginLeft:"1px"}}>
                      Species
                    </Col>
                    <Col style={{minWidth:"55px", maxWidth:"55px"}} className="pe-0">
                      Count
                    </Col>
                    <Col style={{minWidth:"50px", maxWidth:"50px"}} className="pe-0">
                      Sex
                    </Col>
                    <Col style={{minWidth:"150px", maxWidth:"150px"}} className="pe-0">
                      Color
                    </Col>
                  </Row>
                  {room.animals.map(animal => (
                  <Card key={animal.id} className="rounded mb-1" style={{height:"32px", marginLeft:"12px", maxWidth:"720px"}}>
                    <div className="row no-gutters flex-nowrap me-0">
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", borderTopLeftRadius:"0.25rem", borderBottomLeftRadius:"0.25rem", minWidth:"50px", maxWidth:"50px", backgroundColor:"#615e5e"}}>
                        <Checkbox
                          id={'select' + animal.id}
                          name={'select' + animal.id}
                          style={{
                            transform: "scale(.75)",
                            marginRight: "1px",
                            marginTop: "-6px",
                            marginBottom: "-5px",
                            paddingLeft:"13px"
                          }}
                          onChange={() => {
                            if (selectedAnimals.includes(animal.id)) {
                              setSelectedAnimals(selectedAnimals.filter(animal_id => animal_id !== animal.id));
                            }
                            else {
                              setSelectedAnimals(selectedAnimals.concat(animal.id));
                            }
                          }}
                        />
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"75px", maxWidth:"75px", backgroundColor:"#615e5e"}} title={animal.id_for_incident}>
                        <Link href={"/" + organization + "/" + incident + "/animals/" + animal.id_for_incident} className="text-link" style={{textDecoration:"none", color:"white"}}>A#{animal.id_for_incident}</Link>
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"250px", maxWidth:"250px", backgroundColor:"#615e5e"}} title={animal.name || "Unknown Name"}>
                        {animal.name || "Unknown Name"}
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"150px", maxWidth:"150px", backgroundColor:"#615e5e"}} title={capitalize(animal.species_string_singular)}>
                        {capitalize(animal.species_string_singular)}
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"55px", maxWidth:"55px", backgroundColor:"#615e5e"}} title={animal.animal_count}>
                        {animal.animal_count}
                      </Col>
                      <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"50px", maxWidth:"50px", backgroundColor:"#615e5e"}} title={animal.sex||"N/A"}>
                        {animal.sex||"N/A"}
                      </Col>
                      <Col className="border" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"150px", maxWidth:"150px", borderTopRightRadius:"0.25rem", borderBottomRightRadius:"0.25rem", backgroundColor:"#615e5e", textTransform:"capitalize"}} title={(animal.pcolor + (animal.pcolor && animal.scolor ? " / " : "") + animal.scolor) || "N/A"}>
                        {(animal.pcolor + (animal.pcolor && animal.scolor ? " / " : "") + animal.scolor) || "N/A"}
                      </Col>
                    </div>
                  </Card>
                  ))}
                </div>
              </Collapse>
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
    ))}
    <Formik
      initialValues={{'shelter':null, 'room':null, 'ids':selectedAnimals}}
      enableReinitialize={true}
      validationSchema={Yup.object({
        shelter: Yup.number().nullable().required('Shelter is required when animal is sheltered'),
        room: Yup.number().nullable(),
      })}
      onSubmit={(values, { setSubmitting }) => {
        axios.patch('/animals/api/animal/mass_transfer_shelters/', values)
        .then(response => {
          let building_data = [...data.buildings];
          let animal_count = data.animal_count;
          building_data.forEach((building, index) => {
            let building_count = building.animal_count;
            building.rooms.forEach(room => {
              room.animals.filter(animal => selectedAnimals.includes(animal.id)).forEach(animal => {
                building_count = building_count - animal.animal_count;
                animal_count = animal_count - animal.animal_count;
                room['animal_count'] = room.animal_count - animal.animal_count;
              });
              room['animals'] = room.animals.filter(animal => !selectedAnimals.includes(animal.id));
            });
            building['animal_count'] = building_count;
          });
          let unroomed_count = data.unroomed_count;
          data.unroomed_animals.filter(animal => selectedAnimals.includes(animal.id)).forEach(animal => {
            unroomed_count = unroomed_count - animal.animal_count;
            animal_count = animal_count - animal.animal_count;
          });
          setData(prevState => ({ ...prevState, "animal_count":animal_count, "unroomed_animals": data.unroomed_animals.filter(animal => !selectedAnimals.includes(animal.id)), "unroomed_count":unroomed_count, "buildings":building_data }));
          setSelectedAnimals([]);
          handleTransferAnimalClose();
        })
        .catch(error => {
          setShowSystemError(true);
        });
      }}
    >
      {formikProps => (
      <Modal show={showTransferAnimalConfirm} onHide={handleTransferAnimalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Transfer Animals</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Please choose a destination shelter for the selected animals.
          </p>
          <Row className="">
            <Col xs="12">
              <DropDown
                label="To Shelter / Room:"
                id="shelter"
                type="text"
                name="shelter"
                options={shelters.options}
                isClearable={false}
                // ref={shelterRef}
                key={`my_unique_shelter_select_key__${formikProps.values.shelter}`}
                onChange={(instance) => {
                  roomRef.current.clearValue();
                  formikProps.setFieldValue("room", '');
                  formikProps.setFieldValue("shelter", instance === null ? '' : instance.value);
                }}
                value={formikProps.values.shelter||''}
              />
            </Col>
          </Row>
          <Row className="mt-3 mb-3">
            <Col xs="12">
              <DropDown
                id="room"
                type="text"
                name="room"
                ref={roomRef}
                key={`my_unique_room_select_key__${formikProps.values.room}`}
                options={shelters.room_options[formikProps.values.shelter] ? shelters.room_options[formikProps.values.shelter] : []}
                isClearable={true}
                value={formikProps.values.room||''}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => {formikProps.submitForm()}}>Transfer</Button>
          <Button variant="secondary" onClick={handleTransferAnimalClose}>Cancel</Button>
        </Modal.Footer>
      </Modal>
      )}
      </Formik>
    </div>
    )
}

export default ShelterAnimalList;
